<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, toRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import type { ThresholdPoint } from "@modules/energy-manager/types/alarme"
import { useAlarmThresholdForm } from "@modules/energy-manager/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            alarm: {
                yes: "Oui",
                no: "Non",
                distinguishWeekend: "Distinguer semaine et week-end :",
                sectionWeek: "Semaine",
                sectionWeekend: "Week-end",
                thresholdOrderError:
                    "La limite basse doit être strictement inférieure à la consigne, elle-même strictement inférieure à la limite haute.",
            },
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    points: ThresholdPoint[]
    disableAction?: boolean
    /** Unité métier (`ConfigVariable.unit`) affichée sur chaque champ seuil. */
    variableUnit?: string
}>()

const emit = defineEmits<{
    "change-points": [payload: ThresholdPoint[]]
}>()

// ================================
// COMPOSABLES
// ================================
const {
    isDistinguishWeekend,
    consigne,
    limhaute,
    limbasse,
    weekConsigne,
    weekLimhaute,
    weekLimbasse,
    weekendConsigne,
    weekendLimhaute,
    weekendLimbasse,
    isUniformNight,
    uniformNightConsigne,
    uniformNightLimhaute,
    uniformNightLimbasse,
    uniformStartNight,
    uniformEndNight,
    weekIsNight,
    weekNightConsigne,
    weekNightLimhaute,
    weekNightLimbasse,
    weekStartNight,
    weekEndNight,
    weekendIsNight,
    weekendNightConsigne,
    weekendNightLimhaute,
    weekendNightLimbasse,
    weekendStartNight,
    weekendEndNight,
    getPointsLength,
    getIsHourlySeries,
    getShowDistinguishToggle,
    getStartNightSelectOptions,
    getEndNightSelectOptions,
    getIsValidThresholdForm,
} = useAlarmThresholdForm(toRef(props, "points"), (payload) =>
    emit("change-points", payload)
)

const { errors, setFieldError } = useForm()

/**
 * Passe à vrai après une action utilisateur sur les seuils (saisie, toggle nuit, etc.).
 * Tant qu’il est faux, les messages d’erreur d’ordre ne s’affichent pas (évite 0/0/0 au chargement).
 */
const hasInteractedWithThresholdOrder = ref(false)

/**
 * Marque le formulaire seuils comme « touché » pour afficher les erreurs d’ordre.
 *
 * @returns void
 */
function onThresholdOrderInteraction() {
    hasInteractedWithThresholdOrder.value = true
}

watch(
    () => props.points.length,
    (len, oldLen) => {
        if (oldLen === undefined) return
        if (oldLen !== len) {
            hasInteractedWithThresholdOrder.value = false
        }
    }
)

/** Noms de champs vee-validate pour les triplets seuils (sync erreurs / affichage). */
const THRESHOLD_ORDER_FIELD_KEYS = [
    "uniformDayConsigne",
    "uniformDayLimhaute",
    "uniformDayLimbasse",
    "uniformNightConsigne",
    "uniformNightLimhaute",
    "uniformNightLimbasse",
    "weekDayConsigne",
    "weekDayLimhaute",
    "weekDayLimbasse",
    "weekNightConsigne",
    "weekNightLimhaute",
    "weekNightLimbasse",
    "weekendDayConsigne",
    "weekendDayLimhaute",
    "weekendDayLimbasse",
    "weekendNightConsigne",
    "weekendNightLimhaute",
    "weekendNightLimbasse",
] as const

/**
 * Indique si limite basse < consigne < limite haute (inégalités strictes).
 *
 * @param limbasse limite basse
 * @param consigne consigne
 * @param limhaute limite haute
 */
function getIsTripletOrdered(
    limbasse: number,
    consigne: number,
    limhaute: number
): boolean {
    return limbasse < consigne && consigne < limhaute
}

/**
 * Construit la carte d’erreurs vee-validate pour l’ordre des seuils selon le mode affiché.
 *
 * @returns clés de champs → message d’erreur
 */
function buildThresholdOrderFieldErrors(): Partial<
    Record<(typeof THRESHOLD_ORDER_FIELD_KEYS)[number], string>
> {
    const msg = t("alarm.thresholdOrderError")
    const out: Partial<
        Record<(typeof THRESHOLD_ORDER_FIELD_KEYS)[number], string>
    > = {}

    const addIfInvalid = (
        prefix:
            | "uniformDay"
            | "uniformNight"
            | "weekDay"
            | "weekNight"
            | "weekendDay"
            | "weekendNight",
        low: number,
        mid: number,
        high: number
    ) => {
        if (getIsTripletOrdered(low, mid, high)) return
        out[`${prefix}Consigne`] = msg
        out[`${prefix}Limhaute`] = msg
        out[`${prefix}Limbasse`] = msg
    }

    if (getPointsLength.value === 0) {
        return out
    }

    if (!isDistinguishWeekend.value) {
        addIfInvalid(
            "uniformDay",
            limbasse.value,
            consigne.value,
            limhaute.value
        )
        if (getIsHourlySeries.value && isUniformNight.value) {
            addIfInvalid(
                "uniformNight",
                uniformNightLimbasse.value,
                uniformNightConsigne.value,
                uniformNightLimhaute.value
            )
        }
    } else {
        addIfInvalid(
            "weekDay",
            weekLimbasse.value,
            weekConsigne.value,
            weekLimhaute.value
        )
        addIfInvalid(
            "weekendDay",
            weekendLimbasse.value,
            weekendConsigne.value,
            weekendLimhaute.value
        )
        if (getIsHourlySeries.value) {
            if (weekIsNight.value) {
                addIfInvalid(
                    "weekNight",
                    weekNightLimbasse.value,
                    weekNightConsigne.value,
                    weekNightLimhaute.value
                )
            }
            if (weekendIsNight.value) {
                addIfInvalid(
                    "weekendNight",
                    weekendNightLimbasse.value,
                    weekendNightConsigne.value,
                    weekendNightLimhaute.value
                )
            }
        }
    }

    return out
}

const getOrderFieldErrors = computed(() => buildThresholdOrderFieldErrors())

watch(
    getOrderFieldErrors,
    (errMap) => {
        for (const key of THRESHOLD_ORDER_FIELD_KEYS) {
            setFieldError(key, errMap[key])
        }
    },
    { deep: true, immediate: true }
)

const getUniformDayFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.uniformDayConsigne,
        limhaute: errors.value.uniformDayLimhaute,
        limbasse: errors.value.uniformDayLimbasse,
    }
})

const getUniformNightFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.uniformNightConsigne,
        limhaute: errors.value.uniformNightLimhaute,
        limbasse: errors.value.uniformNightLimbasse,
    }
})

const getWeekDayFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.weekDayConsigne,
        limhaute: errors.value.weekDayLimhaute,
        limbasse: errors.value.weekDayLimbasse,
    }
})

const getWeekNightFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.weekNightConsigne,
        limhaute: errors.value.weekNightLimhaute,
        limbasse: errors.value.weekNightLimbasse,
    }
})

const getWeekendDayFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.weekendDayConsigne,
        limhaute: errors.value.weekendDayLimhaute,
        limbasse: errors.value.weekendDayLimbasse,
    }
})

const getWeekendNightFieldErrors = computed(() => {
    if (!hasInteractedWithThresholdOrder.value) {
        return {
            consigne: undefined,
            limhaute: undefined,
            limbasse: undefined,
        }
    }
    return {
        consigne: errors.value.weekendNightConsigne,
        limhaute: errors.value.weekendNightLimhaute,
        limbasse: errors.value.weekendNightLimbasse,
    }
})

/** Formulaire seuil utilisable (règle métier historique + ordre limite basse / consigne / limite haute). */
const getIsValidThresholdFormExpose = computed(
    () =>
        getIsValidThresholdForm.value &&
        Object.keys(getOrderFieldErrors.value).length === 0
)

const getDistinguishBooleanOptions = computed(() => [
    { label: t("alarm.yes"), value: true },
    { label: t("alarm.no"), value: false },
])

defineExpose({ isValidThresholdForm: getIsValidThresholdFormExpose })
</script>

<template>
    <div class="alarm-form flex flex-col gap-4">
        <!-- ================================ -->
        <!-- Mode semaine / week-end -->
        <!-- ================================ -->
        <div
            v-if="getShowDistinguishToggle"
            class="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
        >
            <span class="text-lg font-semibold text-primary">
                {{ t("alarm.distinguishWeekend") }}
            </span>
            <BaseSelectButton
                v-model="isDistinguishWeekend"
                :options="getDistinguishBooleanOptions"
                option-label="label"
                option-value="value"
                :disabled="disableAction || getPointsLength === 0"
                @update:model-value="onThresholdOrderInteraction"
            />
        </div>

        <!-- ================================ -->
        <!-- Profil uniforme -->
        <!-- ================================ -->
        <AlarmFormPeriodSection
            v-if="!isDistinguishWeekend"
            period-key="uniform"
            :use-card="false"
            :is-hourly-series="getIsHourlySeries"
            night-toggle-variant="uniform"
            :variable-unit="variableUnit"
            :disabled="disableAction || getPointsLength === 0"
            v-model:consigne="consigne"
            v-model:limhaute="limhaute"
            v-model:limbasse="limbasse"
            v-model:is-night="isUniformNight"
            v-model:start-night="uniformStartNight"
            v-model:end-night="uniformEndNight"
            v-model:night-consigne="uniformNightConsigne"
            v-model:night-limhaute="uniformNightLimhaute"
            v-model:night-limbasse="uniformNightLimbasse"
            :start-night-options="getStartNightSelectOptions"
            :end-night-options="getEndNightSelectOptions"
            :day-threshold-errors="getUniformDayFieldErrors"
            :night-threshold-errors="getUniformNightFieldErrors"
            @change="onThresholdOrderInteraction"
        />

        <!-- ================================ -->
        <!-- Semaine et week-end distingués -->
        <!-- ================================ -->
        <div v-else class="flex flex-col gap-4">
            <AlarmFormPeriodSection
                period-key="week"
                :use-card="true"
                :section-title="t('alarm.sectionWeek')"
                :is-hourly-series="getIsHourlySeries"
                night-toggle-variant="week"
                :variable-unit="variableUnit"
                :disabled="disableAction || getPointsLength === 0"
                v-model:consigne="weekConsigne"
                v-model:limhaute="weekLimhaute"
                v-model:limbasse="weekLimbasse"
                v-model:is-night="weekIsNight"
                v-model:start-night="weekStartNight"
                v-model:end-night="weekEndNight"
                v-model:night-consigne="weekNightConsigne"
                v-model:night-limhaute="weekNightLimhaute"
                v-model:night-limbasse="weekNightLimbasse"
                :start-night-options="getStartNightSelectOptions"
                :end-night-options="getEndNightSelectOptions"
                :day-threshold-errors="getWeekDayFieldErrors"
                :night-threshold-errors="getWeekNightFieldErrors"
                @change="onThresholdOrderInteraction"
            />
            <AlarmFormPeriodSection
                period-key="weekend"
                :use-card="true"
                :section-title="t('alarm.sectionWeekend')"
                :is-hourly-series="getIsHourlySeries"
                night-toggle-variant="weekend"
                :variable-unit="variableUnit"
                :disabled="disableAction || getPointsLength === 0"
                v-model:consigne="weekendConsigne"
                v-model:limhaute="weekendLimhaute"
                v-model:limbasse="weekendLimbasse"
                v-model:is-night="weekendIsNight"
                v-model:start-night="weekendStartNight"
                v-model:end-night="weekendEndNight"
                v-model:night-consigne="weekendNightConsigne"
                v-model:night-limhaute="weekendNightLimhaute"
                v-model:night-limbasse="weekendNightLimbasse"
                :start-night-options="getStartNightSelectOptions"
                :end-night-options="getEndNightSelectOptions"
                :day-threshold-errors="getWeekendDayFieldErrors"
                :night-threshold-errors="getWeekendNightFieldErrors"
                @change="onThresholdOrderInteraction"
            />
        </div>
    </div>
</template>
